// ONE company search source for every picker and dropdown (strict dedupe). Reads only; never writes.
// It is ONE database round trip (#2793 item 9): the company_picker function (migration 2535) does the whole job on the database side, with the same rules as
// src/lib/companyName.js: live companies only, a merged-away or aliased name (Occidental -> Oxy) resolves to its survivor, names that collide once normalized are one row,
// ranked exact > alias > prefix > contains, and near-matches (a typo, an extra word) come back separately as `similar` so a caller can show "Similar" before any create.
// Debounce the caller: the function reads every company name (about 280 ms).
import { companyKey, splitMatches } from './companyName';

const COLUMNS = 'id, name, company_type, created_at';
const cleanLike = (s) => String(s || '').trim().replace(/[%_,()]/g, ' ').replace(/\s+/g, ' ').trim();

const clean = (s) => String(s || '').trim().replace(/\s+/g, ' ');
const row = (r) => ({ id: r.company_id, name: r.name, domain: r.domain || null, people_count: r.people_count == null ? null : Number(r.people_count), matchKind: r.match_kind || null, viaAlias: r.via_alias === true || r.match_kind === 'alias' });

// supabase: the client. term: what was typed. Returns { matches, similar, error }. matches are capped at `limit`, similar at `similarLimit`.
// withType: also read company_type for the rows (one small keyed read), for a list that shows it.
export async function searchCompaniesStrict(supabase, term, { limit = 8, similarLimit = 4, minLength = 2, withType = false } = {}) {
  const t = clean(term);
  if (t.length < minLength) return { matches: [], similar: [], error: null };
  let out = null;
  try {
    const r = await supabase.rpc('company_picker', { q: t, max_rows: limit });
    if (r && !r.error && r.data && Array.isArray(r.data.matches)) out = r.data;
  } catch (e) { out = null; }
  // the function missing or failing is not "no match": read the same answer the long way (several reads) rather than fail the picker
  if (!out) return legacySearch(supabase, term, { limit, similarLimit, minLength });
  const matches = (out.matches || []).slice(0, limit).map(row);
  const similar = (out.similar || []).slice(0, similarLimit).map(row);
  if (withType && matches.length + similar.length) {
    try {
      const ids = [...matches, ...similar].map((m) => m.id);
      const ty = await supabase.from('companies').select('id, company_type').in('id', ids);
      const byId = new Map((ty.data || []).map((c) => [c.id, c.company_type]));
      matches.forEach((m) => { m.company_type = byId.get(m.id) || null; });
      similar.forEach((m) => { m.company_type = byId.get(m.id) || null; });
    } catch (e) { /* the type is a bonus */ }
  }
  return { matches, similar, error: null };
}

// The long way: live rows by name, near names by first letters, merged-away names to their survivor, the alias function. Used only when company_picker is not there.
async function legacySearch(supabase, term, { limit, similarLimit, minLength }) {
  const t = cleanLike(term);
  if (t.length < minLength) return { matches: [], similar: [], error: null };
  const pool = [];
  try {
    // 1. live companies whose name contains the typed text
    const live = await supabase.from('companies').select(COLUMNS).is('merged_into_id', null).is('deleted_at', null).ilike('name', `%${t}%`).order('name').limit(60);
    if (live.error) return { matches: [], similar: [], error: live.error.message || 'The company search failed.' };
    pool.push(...(live.data || []));

    // 2. close names for the "similar" list: live companies that start with the same two letters (a typo or an extra word later in the name), narrowed by the
    // similarity rule in companyName.js. Best effort: a failure here only means no suggestions, never a failed search.
    try {
      if (t.length >= 4) {
        const near = await supabase.from("companies").select(COLUMNS).is("merged_into_id", null).is("deleted_at", null).ilike("name", `${t.slice(0, 2)}%`).order("name").limit(150);
        if (!near.error) pool.push(...(near.data || []));
      }
    } catch (e) { /* no suggestions */ }

    // 3. an old, merged-away name finds its survivor (best effort as well)
    try {
      const gone = await supabase.from("companies").select("id, name, merged_into_id").not("merged_into_id", "is", null).ilike("name", `%${t}%`).limit(10);
      if (!gone.error && gone.data && gone.data.length) {
        const survivorIds = [...new Set(gone.data.map((r) => r.merged_into_id))].filter((id) => !pool.some((p) => p.id === id));
        if (survivorIds.length) {
          const sv = await supabase.from("companies").select(COLUMNS).in("id", survivorIds).is("merged_into_id", null).is("deleted_at", null);
          if (!sv.error) pool.push(...(sv.data || []).map((r) => ({ ...r, viaAlias: true })));
        }
      }
    } catch (e) { /* the survivor lookup is a bonus */ }
    // 4. #2769: a known alias (Occidental, Occidental Petroleum Corp., Oxy USA Inc) finds its company: the database's resolve_company_name decides (best effort)
    try {
      const r = await supabase.rpc('resolve_company_name', { p_name: t });
      const id = r && !r.error && typeof r.data === 'string' ? r.data : null;
      if (id && !pool.some((p) => p.id === id)) {
        const co = await supabase.from('companies').select(COLUMNS).eq('id', id).is('merged_into_id', null).is('deleted_at', null).maybeSingle();
        if (!co.error && co.data) pool.push({ ...co.data, viaAlias: true });
      } else if (id) {
        const at = pool.findIndex((p) => p.id === id);
        if (at >= 0) pool[at] = { ...pool[at], viaAlias: true };
      }
    } catch (e) { /* the alias lookup is a bonus */ }
  } catch (e) {
    return { matches: [], similar: [], error: (e && e.message) || 'The company search failed.' };
  }
  const seen = new Set();
  const rows = pool.filter((r) => (seen.has(r.id) ? false : (seen.add(r.id), true)));
  const { matches, similar } = splitMatches(t, rows);
  // an old name typed exactly: its survivor is a match even when the names differ
  const aliasHits = rows.filter((r) => r.viaAlias && !matches.some((m) => m.id === r.id));
  const all = [...matches, ...aliasHits];
  return { matches: all.slice(0, limit), similar: similar.slice(0, similarLimit), error: null };
}

// The company a typed name is another name for (Occidental, Occidental Petroleum Corp., Oxy USA Inc -> Oxy), by the database's resolve_company_name, or null. Pickers whose own
// search is a plain name match add this one to their results (#2793). Best effort: a failure here is no alias, never a failed search.
export async function aliasCompanyFor(supabase, term, { columns = 'id, name' } = {}) {
  const t = clean(term);
  if (t.length < 2 || !supabase || typeof supabase.rpc !== 'function') return null;
  try {
    const r = await supabase.rpc('resolve_company_name', { p_name: t });
    const id = r && !r.error && typeof r.data === 'string' ? r.data : null;
    if (!id) return null;
    const co = await supabase.from('companies').select(columns).eq('id', id).is('merged_into_id', null).is('deleted_at', null).maybeSingle();
    return !co.error && co.data ? co.data : null;
  } catch (e) { return null; }
}
// Rows with the alias company first (once), for a picker's result list.
export const withAliasCompany = (rows, alias) => (alias && !(rows || []).some((r) => r.id === alias.id) ? [alias, ...(rows || [])] : (rows || []));

// Is there already a live company with this exact normalized name? Returns it, or null. Used as the last check before a create.
export async function findExistingCompanyStrict(supabase, name) {
  const { matches, error } = await searchCompaniesStrict(supabase, name, { limit: 20, similarLimit: 0 });
  if (error) return { error, company: null };
  const k = companyKey(name);
  return { error: null, company: matches.find((m) => companyKey(m.name) === k) || null };
}
